import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useProvince } from "@/contexts/ProvinceContext";
import { Calendar, MapPin, Plus } from "lucide-react";

export default function EventsBlock() {
  const { selected } = useProvince();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    // Only upcoming events (today onward) so old events don't crowd the homepage.
    const today = new Date().toISOString().slice(0, 10);
    const q = new URLSearchParams({ limit: "6", date_from: today });
    if (selected?.id) q.set("province_id", selected.id);
    api
      .get(`/events?${q.toString()}`)
      .then((r) => {
        if (cancelled) return;
        setItems(r.data || []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setItems([]);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selected?.id]);

  // Render the block even when empty — gives admins/visitors visibility into
  // the module and an easy way to add the first event.
  return (
    <div className="w-full bg-white rounded-lg border border-gray-100 p-5" data-testid="home-events-block">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-[#e11d2a]" />
          <h3 className="text-lg font-black">ETKİNLİKLER</h3>
          {selected?.name && (
            <span className="text-[10px] uppercase tracking-wider text-gray-500">
              {selected.name}
            </span>
          )}
        </div>
        <Link
          to="/etkinlikler"
          className="text-xs font-semibold text-[#e11d2a] hover:underline"
          data-testid="home-events-all"
        >
          Tümünü Gör →
        </Link>
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-20 bg-gray-50 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div
          className="border border-dashed border-gray-200 rounded-lg p-6 text-center"
          data-testid="home-events-empty"
        >
          <Calendar className="w-8 h-8 mx-auto text-gray-300 mb-2" />
          <div className="text-sm font-semibold text-gray-700">
            Yakında planlanmış etkinlik bulunmuyor
          </div>
          <p className="text-xs text-gray-500 mt-1">
            İlinizde bir etkinlik mi var? Hemen bildirin, onaydan sonra burada yer alsın.
          </p>
          <Link
            to="/etkinlikler"
            className="inline-flex items-center gap-1 mt-3 px-3 py-1.5 text-xs font-semibold text-white bg-[#e11d2a] rounded-md hover:bg-[#c01825]"
          >
            <Plus className="w-3 h-3" /> Etkinlik Bildir
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((e) => (
            <Link
              key={e.id}
              to={`/etkinlikler/${e.id}`}
              className="flex gap-3 border border-gray-100 rounded-lg p-3 hover:border-[#e11d2a] hover:bg-red-50/30 transition"
            >
              <div className="shrink-0 w-14 text-center">
                <div className="text-2xl font-black text-[#e11d2a] leading-none">
                  {new Date(e.start_at).getDate()}
                </div>
                <div className="text-[10px] uppercase font-semibold text-gray-600 mt-1">
                  {new Date(e.start_at).toLocaleString("tr-TR", { month: "short" })}
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm line-clamp-2">{e.title}</div>
                <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {e.venue || "—"}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
